import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import UserNavbar from '../components/UserNavbar';
import DoctorNavbar from '../components/DoctorNavbar';
import DOMPurify from 'dompurify';
import QuillEditor from '../components/QuillEditor';
import { toast } from 'react-toastify';
import PostCommentModal from '../components/PostCommentModal';

const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "bullet" }, { list: "ordered" }],
    ["link", "image", "clean"],
  ],
};

function PostDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [doctorId, setDoctorId] = useState(null);
  const [isDoctor, setIsDoctor] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);

  // const toggleOptionsDropdown = () => {
  //   setShowOptionsDropdown(!showOptionsDropdown);
  // };

  const [editForm, setEditForm] = useState({
    title: '',
    large_content: '',
    image: null,
    imagePreview: null
  });

  // Calculate reading time
  const getReadingTime = (content) => {
    if (!content) return 0;
    const wordsPerMinute = 200;
    const text = content.replace(/<[^>]*>/g, '');
    const noOfWords = text.split(/\s+/g).length;
    const minutes = noOfWords / wordsPerMinute;
    return Math.ceil(minutes);
  };

  useEffect(() => {
    checkSession();
  }, [id]);

  useEffect(() => {
    if (id && (userId || doctorId)) {
      fetchPost();
      // Set up polling for real-time updates
      const interval = setInterval(fetchPost, 5000);
      return () => clearInterval(interval);
    }
  }, [id, userId, doctorId]);

  const getImageUrl = (image) => {
    if (!image) return null;
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }
    if (image.startsWith('/media/')) {
      return `${apiUrl}${image}`;
    }
    return `${apiUrl}/media/${image}`;
  };

  // Check if edit mode should be enabled from URL parameter
  useEffect(() => {
    const editParam = searchParams.get('edit');
    if (editParam === 'true' && post && !isEditing && isDoctor && doctorId) {
      // Check if current doctor owns this post
      const isOwner = (
        (post.doctor && (post.doctor === parseInt(doctorId) || post.doctor === doctorId)) ||
        (post.doctor_id && (post.doctor_id === parseInt(doctorId) || post.doctor_id === doctorId))
      );
      if (isOwner) {
        setIsEditing(true);
        setEditForm({
          title: post.title || '',
          large_content: post.large_content || '',
          image: null,
          imagePreview: post.image ? getImageUrl(post.image) : null
        });
        // Remove edit parameter from URL
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('edit');
        navigate(`/post/${id}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`, { replace: true });
      }
    }
  }, [post, searchParams, isDoctor, doctorId, id, navigate, isEditing]);

  const checkSession = async () => {
    try {
      // Check if user
      try {
        const userRes = await api.get('user/session-check/');
        if (userRes.data.authenticated) {
          setUserId(userRes.data.user_id);
          setIsDoctor(false);
          return;
        }
      } catch { }

      // Check if doctor
      try {
        const doctorRes = await api.get('doctor/session-check/');
        if (doctorRes.data.authenticated) {
          setDoctorId(doctorRes.data.doctor_id);
          setIsDoctor(true);
          return;
        }
      } catch { }

      // Not authenticated
      navigate('/user/login');
    } catch (err) {
      console.error("Session check failed:", err);
      navigate('/user/login');
    }
  };

  const fetchPost = async () => {
    try {
      const params = userId ? { user_id: userId } : { doctor_id: doctorId };
      const res = await api.get(`social/posts/${id}/`, { params });
      setPost(res.data);
      setIsLiked(res.data.is_liked || false);
      setIsSaved(res.data.is_saved || false);

      if (isEditing) {
        setEditForm({
          title: res.data.title || '',
          large_content: res.data.large_content || '',
          image: null,
          imagePreview: res.data.image ? getImageUrl(res.data.image) : null
        });
      }
    } catch (err) {
      console.error("Error fetching post:", err);
      toast.error("Failed to load post. Please try again.", {
        position: "bottom-right",
        autoClose: 3000,
      });
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    const newLikedState = !isLiked;
    setIsLiked(newLikedState);

    // Optimistic update
    setPost(prev => ({
      ...prev,
      likes_count: newLikedState
        ? (prev.likes_count || 0) + 1
        : Math.max(0, (prev.likes_count || 0) - 1)
    }));

    try {
      const data = userId ? { user_id: userId } : { doctor_id: doctorId };
      await api.post(`social/posts/${id}/like/`, data);
      fetchPost();
    } catch (err) {
      console.error("Error liking post:", err);
      // Revert on error
      setIsLiked(!newLikedState);
      setPost(prev => ({
        ...prev,
        likes_count: newLikedState
          ? Math.max(0, (prev.likes_count || 0) - 1)
          : (prev.likes_count || 0) + 1
      }));
    }
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;

    try {
      const data = userId
        ? { user_id: userId, content: commentText }
        : { doctor_id: doctorId, content: commentText };
      await api.post(`social/posts/${id}/add_comment/`, data);
      setCommentText('');
      await fetchPost();
    } catch (err) {
      console.error("Error adding comment:", err);
      toast.error("Failed to add comment. Please try again.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  const handleShare = async () => {
    try {
      const res = await api.get(`social/posts/${id}/generate_share_link/`);
      if (res.data.share_link) {
        navigator.clipboard.writeText(res.data.share_link);
        toast.success("Share link copied to clipboard!", {
          position: "bottom-right",
          autoClose: 3000,
        });
        fetchPost();
      }
    } catch (err) {
      console.error("Error generating share link:", err);
      toast.error("Failed to generate share link. Please try again.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  const handleSave = async () => {
    const newSavedState = !isSaved;
    setIsSaved(newSavedState);

    try {
      const data = userId ? { user_id: userId } : { doctor_id: doctorId };
      await api.post(`social/posts/${id}/save/`, data);
      await fetchPost();
      toast.success(newSavedState ? 'Post saved!' : 'Post removed from saved', {
        position: 'bottom-right',
        autoClose: 2000,
      });
    } catch (err) {
      console.error('Error saving post:', err);
      setIsSaved(!newSavedState);
      toast.error('Failed to save post. Please try again.', {
        position: 'bottom-right',
        autoClose: 3000,
      });
    }
  };

  const handleCommentClick = () => {
    setShowCommentModal(true);
  };

  const closeCommentModal = () => {
    setShowCommentModal(false);
  };

  const handleEdit = () => {
    if (!post) return;
    setIsEditing(true);
    setEditForm({
      title: post.title || '',
      large_content: post.large_content || '',
      image: null,
      imagePreview: post.image ? getImageUrl(post.image) : null
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.warning("Image size should be less than 5MB", {
          position: "bottom-right",
          autoClose: 3000,
        });
        return;
      }
      setEditForm(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditForm(prev => ({ ...prev, imagePreview: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm.title.trim()) {
      toast.warning("Please enter a title.", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    if (!editForm.large_content.trim() || editForm.large_content === '<p><br></p>') {
      toast.warning("Please enter some content.", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('doctor_id', doctorId);
      formData.append('title', editForm.title);
      formData.append('short_content', ''); // Empty short content
      formData.append('large_content', DOMPurify.sanitize(editForm.large_content));

      if (editForm.image) {
        formData.append('image', editForm.image);
      }

      await api.patch(`social/posts/${id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success("Post updated successfully!", {
        position: "bottom-right",
        autoClose: 3000,
      });
      setIsEditing(false);
      fetchPost();
    } catch (err) {
      console.error("Error updating post:", err);
      toast.error(err.response?.data?.error || "Failed to update post. Please try again.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({
      title: '',
      large_content: '',
      image: null,
      imagePreview: null
    });
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      // Send doctor_id as query parameter (more reliable for DELETE requests)
      await api.delete(`social/posts/${id}/?doctor_id=${doctorId}`);
      toast.success("Post deleted successfully!", {
        position: "bottom-right",
        autoClose: 3000,
      });
      navigate('/doctor/posts');
    } catch (err) {
      console.error("Error deleting post:", err);
      toast.error(err.response?.data?.error || "Failed to delete post. Please try again.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-light min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="bg-light min-vh-100">
        {isDoctor ? <DoctorNavbar /> : <UserNavbar />}
        <div className="container py-5 text-center">
          <h3>Post not found</h3>
          <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  // Check if current doctor can edit this post
  const canEdit = isDoctor && doctorId && post && (
    (post.doctor && (post.doctor === parseInt(doctorId) || post.doctor === doctorId)) ||
    (post.doctor_id && (post.doctor_id === parseInt(doctorId) || post.doctor_id === doctorId))
  );

  return (
    <div className="bg-white min-vh-100 pb-5">
      {isDoctor ? <DoctorNavbar /> : <UserNavbar />}

      {/* CUSTOM STYLES - ENHANCED FLIPKART STYLE */}
      <style>{`
        .flipkart-container { 
          background: linear-gradient(to bottom, #f5f7fa 0%, #f1f3f6 100%);
          min-height: 100vh;
        }
        .product-image-container { 
          background: #fff; 
          border-radius: 12px; 
          padding: 24px;
          position: sticky;
          top: 20px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.08);
          transition: all 0.3s ease;
        }
        .product-image-container:hover {
          box-shadow: 0 4px 20px rgba(0,0,0,0.12);
        }
        .product-image-main {
          width: 100%;
          height: 400px;
          object-fit: contain;
          cursor: zoom-in;
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 8px;
        }
        .product-image-main:hover {
          transform: scale(1.08);
        }
        .product-details-container {
          background: #fff;
          border-radius: 12px;
          padding: 32px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.08);
          transition: all 0.3s ease;
        }
        .product-details-container:hover {
          box-shadow: 0 4px 20px rgba(0,0,0,0.12);
        }
        .product-title {
          font-size: 28px;
          font-weight: 600;
          color: #1a1a1a;
          line-height: 1.3;
          margin-bottom: 16px;
          letter-spacing: -0.5px;
        }
        .doctor-info-card {
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          border-radius: 10px;
          padding: 16px;
          border: 1px solid #e9ecef;
        }
        .engagement-stats-card {
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          border-radius: 10px;
          padding: 20px;
          border: 1px solid #e9ecef;
        }
        .stat-item {
          transition: all 0.3s ease;
          padding: 12px;
          border-radius: 8px;
        }
        .stat-item:hover {
          background: #f0f0f0;
          transform: translateY(-2px);
        }
        .action-btn-flipkart {
          padding: 14px 28px;
          font-size: 15px;
          font-weight: 600;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .action-btn-flipkart:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        .action-btn-flipkart:active {
          transform: translateY(0);
        }
        .btn-like-active {
          background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
          color: white;
        }
        .btn-like-inactive {
          background: linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%);
          color: #212121;
        }
        .btn-comment {
          background: linear-gradient(135deg, #2874f0 0%, #1e5fd9 100%);
          color: white;
        }
        .btn-share {
          background: linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%);
          color: #212121;
        }
        .description-section {
          background: #fff;
          border-radius: 12px;
          padding: 32px;
          margin-top: 24px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.08);
        }
        .section-title {
          font-size: 22px;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 3px solid #2874f0;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .section-title::before {
          content: '';
          width: 4px;
          height: 24px;
          background: linear-gradient(135deg, #2874f0 0%, #1e5fd9 100%);
          border-radius: 2px;
        }
        .comment-card {
          background: linear-gradient(135deg, #fafbfc 0%, #ffffff 100%);
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 16px;
          border: 1px solid #e9ecef;
          transition: all 0.3s ease;
          box-shadow: 0 1px 4px rgba(0,0,0,0.05);
        }
        .comment-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          transform: translateX(4px);
          border-color: #2874f0;
        }
        .comment-input-wrapper {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 4px;
          border: 2px solid #e9ecef;
          transition: all 0.3s ease;
        }
        .comment-input-wrapper:focus-within {
          border-color: #2874f0;
          box-shadow: 0 0 0 3px rgba(40, 116, 240, 0.1);
        }
        .back-btn-enhanced {
          transition: all 0.3s ease;
          padding: 8px 16px;
          border-radius: 8px;
        }
        .back-btn-enhanced:hover {
          background: #f0f0f0;
          transform: translateX(-4px);
        }
        .post-content {
          line-height: 1.9;
        }
        .post-content p {
          margin-bottom: 1.2rem;
        }
        .post-content h1, .post-content h2, .post-content h3 {
          margin-top: 1.5rem;
          margin-bottom: 1rem;
          color: #1a1a1a;
        }
        .post-content ul, .post-content ol {
          padding-left: 1.5rem;
          margin-bottom: 1.2rem;
        }
        .post-content li {
          margin-bottom: 0.5rem;
        }
        .post-content img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 1.5rem 0;
        }
        .post-content a {
          color: #2874f0;
          text-decoration: none;
          font-weight: 500;
        }
        .post-content a:hover {
          text-decoration: underline;
        }
        .badge-enhanced {
          padding: 6px 12px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 12px;
        }
        // .custom-dropdown-menu {
        //   border-radius: 12px;
        //   border: none;
        //   box-shadow: 0 4px 20px rgba(0,0,0,0.15);
        //   padding: 8px;
        //   animation: fadeIn 0.2s ease-in-out;
        // }
        // .custom-dropdown-item {
        //   border-radius: 8px;
        //   font-weight: 500;
        //   padding: 10px 16px;
        //   transition: all 0.2s;
        // }
        // .custom-dropdown-item:hover {
        //   background-color: #f8f9fa;
        //   transform: translateX(4px);
        // }
        // .custom-dropdown-item.text-danger:hover {
        //   background-color: #fef2f2;
        // }
        // @keyframes fadeIn {
        //   from { opacity: 0; transform: translateY(-10px); }
        //   to { opacity: 1; transform: translateY(0); }
        // }
      `}</style>

      <div className="flipkart-container py-4">
        <div className="container">
          {/* BACK BUTTON */}
          <div className="mb-4">
            <button
              className="back-btn-enhanced btn btn-link text-decoration-none d-inline-flex align-items-center fw-semibold"
              onClick={() => navigate(-1)}
              style={{ color: '#2874f0', fontSize: '15px' }}
            >
              <i className="bi bi-arrow-left me-2 fs-6"></i>
              <span>Back to Feed</span>
            </button>
          </div>

          {!isEditing ? (
            /* VIEW MODE - FLIPKART STYLE */
            <>
              <div className="row g-3">
                {/* LEFT COLUMN - PRODUCT IMAGE */}
                <div className="col-lg-6">
                  <div className="product-image-container shadow-sm h-100">
                    {post.image ? (
                      <div className="text-center h-100 d-flex flex-column justify-content-center">
                        <img
                          src={getImageUrl(post.image)}
                          alt={post.title}
                          className="product-image-main"
                          onClick={() => window.open(getImageUrl(post.image), '_blank')}
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/600x500?text=Health+Post+Image';
                          }}
                        />
                        <div className="mt-3 text-center">
                          <small className="text-muted">
                            <i className="bi bi-zoom-in me-1"></i>
                            Click to view full size
                          </small>
                        </div>
                      </div>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center h-100" style={{ minHeight: '400px' }}>
                        <div className="text-center text-muted">
                          <i className="bi bi-image fs-1 d-block mb-2"></i>
                          <p className="mb-0">No Image Available</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* RIGHT COLUMN - PRODUCT DETAILS */}
                <div className="col-lg-6">
                  <div className="product-details-container shadow-sm h-100">
                    {/* TITLE */}
                    {post.title && (
                      <h1 className="product-title">{post.title}</h1>
                    )}

                    {/* DOCTOR INFO */}
                    <div className="doctor-info-card mb-4">
                      <div className="d-flex align-items-center gap-3">
                        {post.doctor_image ? (
                          <img
                            src={getImageUrl(post.doctor_image)}
                            alt={post.doctor_name}
                            className="rounded-circle border border-2 border-primary"
                            style={{ width: '56px', height: '56px', objectFit: 'cover', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                          />
                        ) : (
                          <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center border border-2 border-primary" style={{ width: '56px', height: '56px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                            <i className="bi bi-person-badge fs-5"></i>
                          </div>
                        )}
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <h5 className="fw-bold text-dark mb-0" style={{ fontSize: '18px' }}>
                              {post.doctor_name || 'Doctor'}
                            </h5>
                            {canEdit && (
                              <i className="bi bi-patch-check-fill text-primary" style={{ fontSize: '18px' }} title="Verified"></i>
                            )}
                          </div>
                          {post.doctor_specialization && (
                            <span className="badge bg-primary bg-opacity-10 text-white badge-enhanced">
                              {post.doctor_specialization}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ENGAGEMENT STATS */}
                    <div className="engagement-stats-card mb-4">
                      <div className="row g-3">
                        <div className="col-6">
                          <div className="stat-item text-center">
                            <div className="fw-bold text-danger mb-1" style={{ fontSize: '24px' }}>
                              <i className="bi bi-heart-fill me-1"></i>
                              {post.likes_count || 0}
                            </div>
                            <small className="text-muted fw-semibold">Likes</small>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="stat-item text-center">
                            <div className="fw-bold text-primary mb-1" style={{ fontSize: '24px' }}>
                              <i className="bi bi-chat-left-text-fill me-1"></i>
                              {post.comments?.length || 0}
                            </div>
                            <small className="text-muted fw-semibold">Comments</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ACTION BUTTONS */}
                    <div className="d-flex flex-wrap gap-3 mb-4">
                      <button
                        className={`action-btn-flipkart flex-grow-1 ${isLiked ? 'btn-like-active' : 'btn-like-inactive'}`}
                        onClick={handleLike}
                      >
                        <i className={`bi ${isLiked ? 'bi-heart-fill' : 'bi-heart'} me-2`}></i>
                        {isLiked ? 'Liked' : 'Like'}
                      </button>
                      <button
                        className="action-btn-flipkart btn-comment flex-grow-1"
                        onClick={handleCommentClick}
                      >
                        <i className="bi bi-chat-left-text me-2"></i>
                        Comment
                      </button>
{/* <button
  className="action-btn-flipkart btn-share"
  onClick={handleShare}
>
  <i className="bi bi-share me-2"></i>
  Share
</button> */}
                      {/* {canEdit && (
                        <div className="dropdown">
                          <button
                            className="action-btn-flipkart btn-share"
                            type="button"
                            onClick={toggleOptionsDropdown}
                            style={{ minWidth: '50px' }}
                          >
                            <i className="bi bi-three-dots-vertical"></i>
                          </button>
                          <ul className={`dropdown-menu custom-dropdown-menu ${showOptionsDropdown ? 'show' : ''}`} style={{ minWidth: '200px' }}>
                            <li>
                              <button className="dropdown-item custom-dropdown-item d-flex align-items-center mb-1" onClick={() => { handleEdit(); setShowOptionsDropdown(false); }}>
                                <i className="bi bi-pencil-square me-3 text-primary fs-5"></i>
                                <span>Edit Post</span>
                              </button>
                            </li>
                            <li><hr className="dropdown-divider my-1 bg-light" /></li>
                            <li>
                              <button className="dropdown-item custom-dropdown-item d-flex align-items-center text-danger mt-1" onClick={() => { handleDeleteClick(); setShowOptionsDropdown(false); }}>
                                <i className="bi bi-trash3 me-3 fs-5"></i>
                                <span>Delete Post</span>
                              </button>
                            </li>
                          </ul>
                        </div>
                      )} */}
                    </div>

                  </div>
                </div>

                {/* FULL DESCRIPTION SECTION */}
                <div className="col-12">
                  <div className="description-section shadow-sm">
                    <h3 className="section-title">
                      <i className="bi bi-file-text-fill text-primary"></i>
                      Description
                    </h3>
                    <div
                      className="post-content"
                      style={{ fontSize: '16px', color: '#2d3436', lineHeight: '1.9' }}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(post.large_content || post.short_content || '')
                      }}
                    />

                    {/* VOICE PLAYER */}
                    {post.voice && (
                      <div className="mt-5 p-4 bg-light rounded-3 border border-2" style={{ borderColor: '#e9ecef' }}>
                        <h6 className="fw-bold mb-3 d-flex align-items-center">
                          <i className="bi bi-mic-fill me-2 text-primary fs-5"></i>
                          Audio Recording
                        </h6>
                        <audio controls className="w-100 rounded" src={getImageUrl(post.voice)} style={{ height: '45px' }} />
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </>
          ) : (
            /* EDIT MODE */
            <div className="card border-0 shadow-sm rounded-4 post-card">
              <div className="card-header bg-white border-0 p-4 pb-0">
                <div className="d-flex align-items-center justify-content-between">
                  <h4 className="fw-bold mb-0">Edit Your Insights</h4>
                  <button className="btn-close" onClick={handleCancelEdit}></button>
                </div>
              </div>
              <div className="card-body p-4 p-md-5">
                <div className="mb-4">
                  <label className="form-label fw-bold text-dark small text-uppercase">Insight Title</label>
                  <input
                    type="text"
                    className="form-control form-control-lg border-2 border-light bg-light bg-opacity-50 focus-ring"
                    value={editForm.title}
                    onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                    maxLength={255}
                    placeholder="Give your post a compelling title..."
                  />
                </div>

                <div className="mb-4">
                  <label className="form-label fw-bold text-dark small text-uppercase">Full Content</label>
                  <div className="quill-modern border-2 border-light rounded-3 overflow-hidden">
                    <QuillEditor
                      value={editForm.large_content}
                      onChange={(value) => setEditForm(prev => ({ ...prev, large_content: value }))}
                      modules={modules}
                      style={{ height: '350px', marginBottom: '50px' }}
                    />
                  </div>
                </div>

                <div className="mb-4 pt-3">
                  <label className="form-label fw-bold text-dark small text-uppercase">Featured Image</label>
                  <div className="d-flex flex-column flex-md-row gap-4 align-items-start">
                    <div className="flex-grow-1 w-100">
                      <label
                        htmlFor="post-image-upload"
                        className="d-flex flex-column align-items-center justify-content-center p-4 border-2 border-dashed rounded-4 bg-light cursor-pointer transition-all hover-bg-light-subtle"
                        style={{ minHeight: '150px' }}
                      >
                        <i className="bi bi-cloud-arrow-up fs-1 text-primary mb-2"></i>
                        <span className="fw-medium text-dark">Click to upload or drag image</span>
                        <span className="text-muted small">JPG, PNG or WEBP (Max 5MB)</span>
                        <input
                          id="post-image-upload"
                          type="file"
                          className="d-none"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </label>
                    </div>
                    {editForm.imagePreview && (
                      <div className="position-relative">
                        <img
                          src={editForm.imagePreview}
                          alt="Preview"
                          className="rounded-4 shadow-sm"
                          style={{ width: '200px', height: '150px', objectFit: 'cover' }}
                        />
                        <button
                          className="btn btn-danger btn-sm rounded-circle position-absolute top-0 end-0 m-2 shadow"
                          onClick={() => setEditForm(prev => ({ ...prev, image: null, imagePreview: null }))}
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="d-flex flex-column flex-md-row gap-3 pt-4 border-top">
                  <button className="btn btn-primary px-5 py-2 rounded-pill fw-bold shadow-sm" onClick={handleSaveEdit}>
                    Save Changes
                  </button>
                  <button className="btn btn-outline-secondary px-5 py-2 rounded-pill fw-bold" onClick={handleCancelEdit}>
                    Cancel
                  </button>
                  <button className="btn btn-link text-danger ms-md-auto text-decoration-none fw-medium" onClick={handleDeleteClick}>
                    <i className="bi bi-trash3 me-2"></i>Delete Post
                  </button>
                </div>
              </div>
            </div>
          )}

          <PostCommentModal
            post={post}
            isOpen={showCommentModal && !!post}
            onClose={closeCommentModal}
            commentText={commentText}
            onCommentTextChange={setCommentText}
            onSubmitComment={handleComment}
            onLike={handleLike}
            // onShare={handleShare}
            onSave={handleSave}
            isLiked={isLiked}
            isSaved={isSaved}
          />

          {/* DELETE CONFIRMATION MODAL */}
          {showDeleteConfirm && (
            <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 shadow-lg rounded-4">
                  <div className="modal-header border-0 pb-0">
                    <h5 className="modal-title fw-bold text-danger">
                      <i className="bi bi-exclamation-triangle me-2"></i>Confirm Delete
                    </h5>
                    <button type="button" className="btn-close" onClick={() => setShowDeleteConfirm(false)}></button>
                  </div>
                  <div className="modal-body p-4">
                    <p className="mb-0">
                      Are you sure you want to delete this post? This action cannot be undone.
                    </p>
                  </div>
                  <div className="modal-footer border-0">
                    <button
                      type="button"
                      className="btn btn-outline-secondary rounded-3"
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger rounded-3 fw-bold"
                      onClick={handleDeleteConfirm}
                    >
                      <i className="bi bi-trash me-2"></i>Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PostDetail;
